import { IPhrases } from '@/types/types/page/pages';
import { isEmpty } from '@/utils/fp/isEmpty/isEmpty';
import { JSXFromString } from '@/utils/JSXFromString';
import { replaceVarsOnTranslation } from '@/utils/replaceVarsOnTranslation';
import { emptyPhrase } from './isEmptyPhrase';
import { noPhraseSymbol } from '@/consts/noPhraseSymbol';
import { config } from '@/config';
import * as Sentry from '@sentry/nextjs';

const isTestEnv = config.nodeEnv === 'test';

const missingPhrases = new Set();

const checkForMissingPhrase = (phraseId: number, phrases?: IPhrases) => {
    if (!missingPhrases.has(phraseId) && !isEmpty(phrases)) {
        Sentry.captureException(new Error('Missing PhraseId - phrase is not recieved via props'), {
            extra: {
                phraseId,
            },
        });
        missingPhrases.add(phraseId);
    }
};

const getPhrase = (
    phraseId: number,
    phrases?: IPhrases,
    options?: {
        replaceTextVars?: (string | number)[];
        asString?: boolean;
    },
) => {
    const phrase = phrases?.[phraseId];
    if (!phrase) {
        checkForMissingPhrase(phraseId, phrases);
        return emptyPhrase;
    }

    if (phrase === noPhraseSymbol) return options?.asString ? '' : emptyPhrase;

    if (options?.replaceTextVars) {
        const phraseWithReplacedVars = replaceVarsOnTranslation(
            phrase,
            options.replaceTextVars.map((x) => String(x)),
        );
        return options?.asString ? phraseWithReplacedVars : JSXFromString(phraseWithReplacedVars);
    }

    return options?.asString ? phrase : JSXFromString(phrase);
};

const findPhrase = (
    phraseId: number,
    phrases?: IPhrases,
    options?: { replaceTextVars?: (string | number)[]; asString?: boolean },
): ReturnType<typeof getPhrase> => {
    return isTestEnv ? String(phraseId) : getPhrase(phraseId, phrases, options);
};

export const usePropsPhrases = (phrases?: IPhrases) => {
    const getPhrase = <B extends boolean>(
        phraseId: number | undefined,
        options?: { replaceTextVars?: (string | number)[]; asString?: B },
    ): B extends true ? string : string | React.ReactElement | React.ReactElement[] => {
        if (!phraseId) {
            return emptyPhrase;
        } else {
            return findPhrase(phraseId, phrases, options) as B extends true
                ? string
                : string | React.ReactElement | React.ReactElement[];
        }
    };

    return { t: getPhrase };
};
